import type { AxiosError } from 'axios';
import axiosInstance from '../../../lib/axios';
import type { ApiResponse } from '../../../services/api';
import type { Project, ProjectFormValues, ProjectList, ProjectUpdateValues } from '../types';

type ErrorBody = { message?: string; error?: string; errorCode?: string };

const request = async <T>(
  run: () => Promise<{ data: { data?: T } }>,
  fallbackError: string
): Promise<ApiResponse<T>> => {
  try {
    const response = await run();
    return { success: true, data: response.data.data };
  } catch (error) {
    const body = (error as AxiosError<ErrorBody>).response?.data;
    return {
      success: false,
      errorCode: body?.errorCode,
      error: body?.message || body?.error || fallbackError,
    };
  }
};

const toPayload = (values: ProjectUpdateValues) => ({
  ...(values.name !== undefined && { name: values.name.trim() }),
  ...(values.description !== undefined && { description: values.description.trim() }),
  ...(values.instructions !== undefined && { instructions: values.instructions.trim() }),
});

const OPTIONS = { skipErrorToast: true } as const;

export const listProjects = () =>
  request<ProjectList>(() => axiosInstance.get('/v1/projects', OPTIONS), 'Failed to load projects');

export const createProject = (values: ProjectFormValues) =>
  request<Project>(
    () => axiosInstance.post('/v1/projects', toPayload(values), OPTIONS),
    'Failed to create project'
  );

export const updateProject = (projectId: string, values: ProjectUpdateValues) =>
  request<Project>(
    () => axiosInstance.patch(`/v1/projects/${projectId}`, toPayload(values), OPTIONS),
    'Failed to update project'
  );

export const deleteProject = (projectId: string) =>
  request<undefined>(
    () => axiosInstance.delete(`/v1/projects/${projectId}`, OPTIONS),
    'Failed to delete project'
  );
